import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import Dashboard from './components/Dashboard';
import ReportsList from './components/ReportsList';
import LoadingSpinner from './components/LoadingSpinner';
import Login from './components/Login';
import AdminPanel from './components/AdminPanel';
import DigitalQaProjectList from './components/DigitalQaProjectList';
import DigitalQaProject from './components/DigitalQaProject';
import DashboardLayout from './components/DashboardLayout';

function AppContent() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [userInfo, setUserInfo] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const expiresAt = localStorage.getItem('authTokenExpiresAt');
    const storedUserInfo = localStorage.getItem('userInfo');
    
    if (token) {
      if (expiresAt) {
        const expirationTime = parseInt(expiresAt, 10);
        const now = Date.now();
        
        if (now > expirationTime) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('authTokenExpiresAt');
          localStorage.removeItem('userInfo');
          return;
        }
      }
      
      setAuthToken(token);
      setIsAuthenticated(true);
      
      if (storedUserInfo) {
        try {
          setUserInfo(JSON.parse(storedUserInfo));
        } catch (e) {
          console.error('Error parsing stored user info:', e);
        }
      }
    }
  }, []);

  const handleLogin = (token, user) => {
    setAuthToken(token);
    setIsAuthenticated(true);
    setUserInfo(user);
    
    if (user) {
      localStorage.setItem('userInfo', JSON.stringify(user));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authTokenExpiresAt');
    localStorage.removeItem('userInfo');
    setIsAuthenticated(false);
    setAuthToken(null);
    setUserInfo(null);
    navigate('/');
  };

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  const reportsListElement = (
    <ReportsList
      onLogout={handleLogout}
      userRole={userInfo?.roles}
    />
  );

  return (
    <DashboardLayout userInfo={userInfo} onLogout={handleLogout}>
      <Routes>
        <Route path="/" element={reportsListElement} />
        <Route path="/:portfolio" element={reportsListElement} />
        <Route path="/:portfolio/:account" element={reportsListElement} />
        <Route 
          path="/admin" 
          element={
            <AdminPanel 
              onLogout={handleLogout}
              initialSection={null}
            />
          } 
        />
        <Route 
          path="/admin/users" 
          element={
            <AdminPanel 
              onLogout={handleLogout}
              initialSection="users"
            />
          } 
        />
        <Route 
          path="/admin/projects" 
          element={
            <AdminPanel 
              onLogout={handleLogout}
              initialSection="projects"
            />
          } 
        />
        <Route 
          path="/digital-qa" 
          element={
            <DigitalQaProjectList 
              onLogout={handleLogout}
            />
          } 
        />
        <Route 
          path="/digital-qa/:projectName" 
          element={
            <DigitalQaProject 
              onLogout={handleLogout}
            />
          } 
        />
        <Route 
          path="/:portfolio/:account/:projectName" 
          element={
            <ProjectReportView 
              authToken={authToken}
              handleLogout={handleLogout}
            />
          } 
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </DashboardLayout>
  );
}

function ProjectReportView({ authToken, handleLogout }) {
  const { portfolio, account, projectName } = useParams();
  const navigate = useNavigate();
  const [localLoading, setLocalLoading] = useState(true);
  const [localError, setLocalError] = useState(null);
  const [reportData, setReportData] = useState(null);

  const handleBack = () => {
    window.scrollTo(0, 0);
    if (portfolio && account) {
      navigate(`/${encodeURIComponent(portfolio)}/${encodeURIComponent(account)}`);
    } else {
      navigate('/');
    }
  };

  useEffect(() => {
    const loadReport = async () => {
      if (authToken && projectName) {
        setLocalLoading(true);
        try {
          const API_URL = process.env.NODE_ENV === 'production' 
            ? '/api' 
            : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');
          
          const reportsResponse = await axios.get(`${API_URL}/reports`, {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          });
          
          const decodedName = decodeURIComponent(projectName);
          const report = reportsResponse.data.find(r => r.projectName === decodedName);
          
          if (!report) {
            setLocalError(`No report found for project: ${decodedName}`);
            setLocalLoading(false);
            return;
          }

          let reportProjectType = report.projectType || '';
          const reportDisplayName = report.displayName || '';
          if (!reportProjectType) {
            const projectsConfigResponse = await axios.get(`${API_URL}/projects-config`, {
              headers: {
                Authorization: `Bearer ${authToken}`,
              },
            });
            const projectConfig = projectsConfigResponse.data.find(p => p.name === decodedName);
            reportProjectType = projectConfig?.projectType || '';
          }
          
          const encodedFilename = report.filename.split('/').map(part => encodeURIComponent(part)).join('/');
          const response = await axios.get(`${API_URL}/reports/${encodedFilename}`, {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          });
          setReportData({
            ...response.data,
            displayName: reportDisplayName,
            projectType: reportProjectType || response.data.projectType || '',
          });
          setLocalLoading(false);
        } catch (err) {
          setLocalError(`Failed to load report: ${err.response?.data?.message || err.message}`);
          setLocalLoading(false);
        }
      }
    };
    
    loadReport();
  }, [authToken, projectName]);

  if (localLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner />
      </div>
    );
  }

  if (localError) {
    return (
      <div className="p-4">
        <div className="p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400">
          {localError}
        </div>
        <button
          onClick={handleBack}
          className="text-white bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 dark:bg-primary-600 dark:hover:bg-primary-700 focus:outline-none dark:focus:ring-primary-800"
        >
          Back
        </button>
      </div>
    );
  }

  if (reportData) {
    return (
      <div className="p-4">
        <div className="mb-4">
          <button
            onClick={handleBack}
            className="inline-flex items-center text-sm font-medium text-primary-700 hover:text-primary-800 dark:text-primary-500 dark:hover:text-primary-400"
          >
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
            Back to projects
          </button>
        </div>
        <Dashboard data={reportData} onLogout={handleLogout} />
      </div>
    );
  }

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
